import { useState, useEffect, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    getCampusGuid,
    getUsername,
    updateSessionCampus,
    getCampusConfig,
} from '../../../services/session.js';
import { getUTCTimestamp, getTTL } from '../../../libs.jsx';
import {
    fetchCampusConfig,
    fetchDeviceNames,
    fetchReasonCodes,
    fetchGlobalReasonCodes,
    fetchServerConfig,
    saveCampusConfig,
    editReasonCodes,
    addReasonCodes,
    deleteReasonCode,
    saveServerSettings,
} from './api/systemSettingsApi.js';

import AppSettingsTab from './components/AppSettingsTab.jsx';
import CustomizationTab from './components/CustomizationTab.jsx';
import AlertSettingsTab from './components/AlertSettingsTab.jsx';
import DeviceSettingsTab from './components/DeviceSettingsTab.jsx';
import ReasonSettingsTab from './components/ReasonSettingsTab.jsx';
import ServerSettingsTab from './components/ServerSettingsTab.jsx';
import DeviceDrawer from './components/DeviceDrawer.jsx';
import ReasonDrawer from './components/ReasonDrawer.jsx';
import DeleteReasonDialog from './components/DeleteReasonDialog.jsx';

import './system-settings-page.css';

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------
const TABS = [
    { key: 'app', label: 'App' },
    { key: 'custom', label: 'Customization' },
    { key: 'alert', label: 'Alerts' },
    { key: 'device', label: 'Devices' },
    { key: 'reason', label: 'Reasons' },
    { key: 'server', label: 'Server' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const SystemSettingsPage = () => {
    // -- Session --
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusList = useMemo(() => {
        const list = getCampusList() || [];
        // Filter to only non-cloud (legacy) campuses
        const session = getSession();
        return list.filter((c) => {
            const cfg = session?.campusData?.[c.campus_id?.toString()];
            return cfg && !cfg.is_cloud;
        });
    }, []);
    const campusName = useMemo(() => {
        const match = campusList.find((c) => c.campus_id === campusId);
        return match?.campus_name || `Campus ${campusId}`;
    }, [campusId, campusList]);

    // -- Tab state --
    const [activeTab, setActiveTab] = useState('app');

    // -- Data --
    const [configData, setConfigData] = useState({});
    const [deviceData, setDeviceData] = useState([]);
    const [reasonData, setReasonData] = useState([]);
    const [allReasonData, setAllReasonData] = useState([]);
    const [globalReasons, setGlobalReasons] = useState([]);
    const [serverData, setServerData] = useState({});

    // -- Loading --
    const [loading, setLoading] = useState(true);

    // -- Saving --
    const [saving, setSaving] = useState(false);

    // -- Drawers / Dialogs --
    const [deviceDrawerOpen, setDeviceDrawerOpen] = useState(false);
    const [reasonDrawerOpen, setReasonDrawerOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: '', index: 0, description: '' });
    const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

    // -- Toast --
    const [toast, setToast] = useState(null);

    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // ======================================================================
    // Data loading
    // ======================================================================
    const loadData = useCallback(async (cId) => {
        setLoading(true);
        const guid = getCampusGuid(cId);

        try {
            const [config, devices, reasons, globalR, server] = await Promise.all([
                fetchCampusConfig(guid),
                fetchDeviceNames(guid),
                fetchReasonCodes(guid),
                fetchGlobalReasonCodes(guid),
                fetchServerConfig(cId),
            ]);

            setConfigData(config || {});
            setDeviceData(devices || []);
            setReasonData(reasons?.active || []);
            setAllReasonData(reasons?.all || []);
            setGlobalReasons(globalR || []);
            setServerData(Array.isArray(server) && server.length > 0 ? server[0] : (server || {}));
        } catch (err) {
            console.error('[SystemSettingsPage] Load error:', err);
            showToast('Failed to load settings data.', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // Initial load
    useEffect(() => {
        loadData(campusId);
    }, [campusId, loadData]);

    // ======================================================================
    // Campus switch handler
    // ======================================================================
    const handleCampusChange = (newId) => {
        const session = getSession();
        const oldIsCloud = session?.campusData?.[campusId?.toString()]?.is_cloud;
        const newIsCloud = session?.campusData?.[newId?.toString()]?.is_cloud;

        setCampusId(newId);
        updateSessionCampus(newId);

        // If switching between cloud and non-cloud, reload the whole page
        if (oldIsCloud !== newIsCloud) {
            window.location.reload();
        }
    };

    // ======================================================================
    // Save handlers
    // ======================================================================
    const handleSaveAppSettings = async (formData) => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();
        const payload = [{
            ...formData,
            CampusId: guid,
            ModifiedBy: getUsername(),
            DateModified: timestamp,
        }];

        const [ok] = await saveCampusConfig(payload, guid);
        setSaving(false);

        if (ok) {
            setConfigData((prev) => ({ ...prev, ...formData }));
            showToast('App settings saved.');
        } else {
            showToast('Failed to save app settings.', 'error');
        }
    };

    const handleSaveCustomSettings = async (formData) => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();
        const payload = [{
            ...formData,
            CampusId: guid,
            ModifiedBy: getUsername(),
            DateModified: timestamp,
        }];

        const [ok] = await saveCampusConfig(payload, guid);
        setSaving(false);

        if (ok) {
            setConfigData((prev) => ({ ...prev, ...formData }));
            showToast('Customization settings saved.');
        } else {
            showToast('Failed to save customization settings.', 'error');
        }
    };

    const handleSaveAlertSettings = async (formData) => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();
        const payload = [{
            ...formData,
            CampusId: guid,
            ModifiedBy: getUsername(),
            DateModified: timestamp,
        }];

        const [ok] = await saveCampusConfig(payload, guid);
        setSaving(false);

        if (ok) {
            setConfigData((prev) => ({ ...prev, ...formData }));
            showToast('Alert settings saved.');
        } else {
            showToast('Failed to save alert settings.', 'error');
        }
    };

    const handleSaveDevices = async (selectedDevices) => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();
        const payload = [{
            AFRDevices: selectedDevices,
            CampusId: guid,
            ModifiedBy: getUsername(),
            DateModified: timestamp,
        }];

        const [ok] = await saveCampusConfig(payload, guid);
        setSaving(false);

        if (ok) {
            setConfigData((prev) => ({ ...prev, AFRDevices: selectedDevices }));
            setDeviceDrawerOpen(false);
            showToast('Device settings saved.');
        } else {
            showToast('Failed to save device settings.', 'error');
        }
    };

    const handleSaveReasonEdits = async (changedPayload) => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();

        const payload = changedPayload.map((item) => ({
            ...item,
            CampusId: guid,
            ModifiedBy: getUsername(),
            DateModified: timestamp,
        }));

        const [ok] = await editReasonCodes(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            showToast('Reason codes updated.');
            // Reload reason data to stay in sync
            const guid2 = getCampusGuid(campusId);
            const reasons = await fetchReasonCodes(guid2);
            setReasonData(reasons?.active || []);
            setAllReasonData(reasons?.all || []);
        } else {
            showToast('Failed to update reason codes.', 'error');
        }
    };

    const handleAddReason = async ({ description, category }) => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();

        // Calculate next available ReasonCodeId
        const usedIds = allReasonData.map((r) => r.ReasonCodeId);
        let nextId = 1;
        while (usedIds.includes(nextId.toString())) {
            nextId++;
        }

        const payload = [{
            ReasonCodeId: nextId.toString(),
            Description: description,
            GlobalReasonCode: category,
            Deleted: false,
            CampusId: guid,
            ModifiedBy: getUsername(),
            DateModified: timestamp,
            DateCreated: timestamp,
        }];

        const [ok] = await addReasonCodes(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            setReasonData((prev) => [...prev, payload[0]]);
            setAllReasonData((prev) => [...prev, payload[0]]);
            setReasonDrawerOpen(false);
            showToast('Reason added.');
        } else {
            showToast('Failed to add reason.', 'error');
        }
    };

    const handleBulkCreate = async () => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();
        const base = { CampusId: guid, ModifiedBy: getUsername(), DateModified: timestamp, DateCreated: timestamp };

        const payload = [
            { ReasonCodeId: '1', Description: 'Medical Emergency', GlobalReasonCode: '18', ...base },
            { ReasonCodeId: '2', Description: 'Toileting', GlobalReasonCode: '2', ...base },
            { ReasonCodeId: '3', Description: 'Mobility', GlobalReasonCode: '8', ...base },
            { ReasonCodeId: '4', Description: 'Dressing/Grooming', GlobalReasonCode: '9', ...base },
            { ReasonCodeId: '5', Description: 'House Keeping', GlobalReasonCode: '15', ...base },
            { ReasonCodeId: '6', Description: 'Medication', GlobalReasonCode: '5', ...base },
            { ReasonCodeId: '7', Description: 'Food/Drink', GlobalReasonCode: '7', ...base },
            { ReasonCodeId: '8', Description: 'Other', GlobalReasonCode: '1', ...base },
        ];

        const [ok] = await addReasonCodes(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);
        setBulkDialogOpen(false);

        if (ok) {
            setReasonData(payload);
            setAllReasonData(payload);
            showToast('Default reason codes created.');
        } else {
            showToast('Failed to create default reason codes.', 'error');
        }
    };

    const handleDeleteReason = async () => {
        setSaving(true);
        const guid = getCampusGuid(campusId);
        const timestamp = getUTCTimestamp();

        const payload = [{
            Deleted: true,
            ReasonCodeId: deleteTarget.id,
            CampusId: guid,
            DateModified: timestamp,
            ModifiedBy: getUsername(),
            TTL: getTTL(7),
        }];

        const [ok] = await deleteReasonCode(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);
        setDeleteDialogOpen(false);

        if (ok) {
            const idx = deleteTarget.index;
            setReasonData((prev) => [...prev.slice(0, idx), ...prev.slice(idx + 1)]);
            showToast('Reason deleted.');
        } else {
            showToast('Failed to delete reason.', 'error');
        }
    };

    const handleSaveServerSettings = async (formData) => {
        setSaving(true);
        const payload = [formData];

        const [ok] = await saveServerSettings(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);

        if (ok) {
            setServerData((prev) => ({ ...prev, ...formData }));
            showToast('Server settings saved.');
        } else {
            showToast('Failed to save server settings.', 'error');
        }
    };

    // ======================================================================
    // Delete reason dialog trigger
    // ======================================================================
    const openDeleteDialog = (id, index, description) => {
        setDeleteTarget({ id, index, description });
        setDeleteDialogOpen(true);
    };

    // Next reason number for the add drawer
    const nextReasonNumber = reasonData.length + 1;

    // ======================================================================
    // Render
    // ======================================================================
    const renderTabContent = () => {
        if (loading) {
            return (
                <div className="pp-section">
                    <div className="pp-section__body">
                        <div className="ss-skeleton" style={{ width: '60%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '80%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px' }} />
                    </div>
                </div>
            );
        }

        return activeTab === 'app' ? (
            <AppSettingsTab
                alertTone={configData?.AlertTone || ''}
                campusTimeZone={configData?.CampusTimeZone ?? 0}
                logOutTimeInterval={configData?.LogOutTimeInterval || 1}
                onSave={handleSaveAppSettings}
                saving={saving}
            />
        ) : activeTab === 'custom' ? (
            <CustomizationTab
                userEditDisplayName={!!configData?.UserEditDisplayName}
                caregiverChgGroupEnabled={!!configData?.CaregiverChgGroupEnabled}
                caregiverChgToneEnabled={!!configData?.CaregiverChgToneEnabled}
                onSave={handleSaveCustomSettings}
                saving={saving}
            />
        ) : activeTab === 'alert' ? (
            <AlertSettingsTab
                acceptEnabled={!!configData?.AcceptButtonEnabled}
                finishEnabled={!!configData?.FinishButtonEnabled}
                reasonEnabled={!!configData?.ReasonCodeEnabled}
                releaseTimeout={configData?.ReleaseTimeInterval || 1}
                finishTimeout={configData?.FinishTimeInterval || 5}
                clearTimeout={configData?.ClearTimeInterval || 4}
                highPriorityTypes={configData?.HighPriorityAlertTypes || []}
                onSave={handleSaveAlertSettings}
                saving={saving}
            />
        ) : activeTab === 'device' ? (
            <DeviceSettingsTab
                afrDevices={configData?.AFRDevices}
                onEditDevices={() => setDeviceDrawerOpen(true)}
            />
        ) : activeTab === 'reason' ? (
            <ReasonSettingsTab
                reasonCodes={reasonData}
                globalReasons={globalReasons}
                onSaveEdits={handleSaveReasonEdits}
                onAddReason={() => setReasonDrawerOpen(true)}
                onBulkCreate={() => setBulkDialogOpen(true)}
                onDeleteReason={openDeleteDialog}
                saving={saving}
            />
        ) : activeTab === 'server' ? (
            <ServerSettingsTab
                emergencyContact={serverData?.emergency_contact || ''}
                onSave={handleSaveServerSettings}
                saving={saving}
            />
        ) : null;
    };

    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ss-page">
                    {/* Toast notification */}
                    {toast ? (
                        <div className={`dp-toast dp-toast--${toast.type}`}>
                            <i className={`fe ${toast.type === 'success' ? 'fe-check-circle' : 'fe-alert-circle'}`} />
                            {toast.message}
                            <button className="dp-toast__close" onClick={() => setToast(null)}>
                                <i className="fe fe-x" />
                            </button>
                        </div>
                    ) : null}

                    {/* Page header */}
                    <div className="dp-page-header">
                        <div className="dp-page-header__inner">
                            <div className="dp-breadcrumb-row">
                                <nav className="dp-breadcrumb">
                                    <span className="dp-breadcrumb__text">Settings</span>
                                    <span className="dp-breadcrumb__separator">/</span>
                                    <span className="dp-breadcrumb__current">System Settings</span>
                                </nav>
                                <div className="dp-campus-row">
                                    <label htmlFor="ss-campus-selector" className="dp-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="ss-campus-selector"
                                            value={campusId}
                                            onChange={(e) => handleCampusChange(parseInt(e.target.value, 10))}
                                            className="dp-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>
                                                    {c.campus_name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="dp-campus-name">{campusName}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tab bar */}
                    <div className="pp-tab-bar">
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Scrollable tab content */}
                    <div className="pp-tab-content">
                        {renderTabContent()}
                    </div>

                    {/* Drawers & Dialogs */}
                    <DeviceDrawer
                        open={deviceDrawerOpen}
                        onClose={() => setDeviceDrawerOpen(false)}
                        onSave={handleSaveDevices}
                        saving={saving}
                        currentDevices={configData?.AFRDevices || []}
                        allDevices={deviceData}
                    />

                    <ReasonDrawer
                        open={reasonDrawerOpen}
                        onClose={() => setReasonDrawerOpen(false)}
                        onSave={handleAddReason}
                        saving={saving}
                        globalReasons={globalReasons}
                        nextReasonNumber={nextReasonNumber}
                    />

                    <DeleteReasonDialog
                        open={deleteDialogOpen}
                        onClose={() => setDeleteDialogOpen(false)}
                        onConfirm={handleDeleteReason}
                        saving={saving}
                        reasonDescription={deleteTarget.description}
                    />

                    {/* Bulk Create Defaults dialog */}
                    {bulkDialogOpen ? (
                        <div className="pp-dialog-backdrop" onClick={() => setBulkDialogOpen(false)}>
                            <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
                                <div className="pp-dialog__header">
                                    <h3 className="pp-dialog__title">Create Default Reason Codes</h3>
                                    <button className="dp-drawer__close" onClick={() => setBulkDialogOpen(false)}>
                                        <i className="fe fe-x" />
                                    </button>
                                </div>
                                <div className="pp-dialog__body">
                                    <p className="pp-dialog__text" style={{ marginBottom: 'var(--space-3)' }}>
                                        Do you wish to add the following default reasons?
                                    </p>
                                    <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', lineHeight: 1.8 }}>
                                        <li>Medical Emergency</li>
                                        <li>Toileting</li>
                                        <li>Mobility</li>
                                        <li>Dressing/Grooming</li>
                                        <li>House Keeping</li>
                                        <li>Medication</li>
                                        <li>Food/Drink</li>
                                        <li>Other</li>
                                    </ul>
                                </div>
                                <div className="pp-dialog__footer">
                                    <button className="dp-btn dp-btn--cancel" onClick={() => setBulkDialogOpen(false)}>
                                        No
                                    </button>
                                    <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleBulkCreate}>
                                        {saving ? 'Creating...' : 'Yes'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default SystemSettingsPage;
